import React, { useMemo } from "react";

import { walletTransactionTypeLabel } from "../../utils/walletTransactionLabels";
import { walletTransactionReportDetails } from "../../utils/walletTransactionReportDetails";

function summarize(transactions) {
  let credits = 0;
  let debits = 0;
  for (const t of transactions) {
    const n = Number(t?.amount);
    if (!Number.isFinite(n)) continue;
    const dir = String(t?.direction ?? "").toUpperCase();
    if (dir === "CREDIT") credits += n;
    else if (dir === "DEBIT") debits += n;
  }
  return { credits, debits, net: credits - debits, count: transactions.length };
}

function formatSignedAmount(row, bdtFull) {
  const n = Number(row?.amount);
  if (!Number.isFinite(n)) return { text: String(row?.amount ?? "—"), className: "" };
  const dir = String(row?.direction ?? "").toUpperCase();
  const abs = bdtFull.format(Math.abs(n));
  if (dir === "CREDIT") return { text: `+${abs}`, className: "wallet-txn-report__amount--in" };
  if (dir === "DEBIT") return { text: `−${abs}`, className: "wallet-txn-report__amount--out" };
  return { text: abs, className: "" };
}

function formatWhen(iso) {
  if (!iso) return { date: "—", time: "" };
  try {
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }),
      time: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit" }),
    };
  } catch {
    return { date: String(iso), time: "" };
  }
}

export default function WalletTransactionsReport({ transactions, bdtFull, periodDays = 7 }) {
  const sorted = useMemo(() => {
    const list = Array.isArray(transactions) ? [...transactions] : [];
    return list.sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0));
  }, [transactions]);

  const summary = useMemo(() => summarize(sorted), [sorted]);

  if (!sorted.length) return null;

  return (
    <div className="wallet-txn-report">
      <div className="wallet-txn-report__summary" role="group" aria-label="Recent movements summary">
        <div className="wallet-txn-report__stat">
          <span className="wallet-txn-report__stat-label">Period</span>
          <span className="wallet-txn-report__stat-value">Last {periodDays} days</span>
        </div>
        <div className="wallet-txn-report__stat">
          <span className="wallet-txn-report__stat-label">Movements</span>
          <span className="wallet-txn-report__stat-value">{summary.count}</span>
        </div>
        <div className="wallet-txn-report__stat wallet-txn-report__stat--in">
          <span className="wallet-txn-report__stat-label">Money in</span>
          <span className="wallet-txn-report__stat-value">+{bdtFull.format(summary.credits)}</span>
        </div>
        <div className="wallet-txn-report__stat wallet-txn-report__stat--out">
          <span className="wallet-txn-report__stat-label">Money out</span>
          <span className="wallet-txn-report__stat-value">−{bdtFull.format(summary.debits)}</span>
        </div>
        <div className="wallet-txn-report__stat wallet-txn-report__stat--net">
          <span className="wallet-txn-report__stat-label">Net change</span>
          <span
            className={`wallet-txn-report__stat-value${
              summary.net >= 0 ? " wallet-txn-report__stat-value--in" : " wallet-txn-report__stat-value--out"
            }`}
          >
            {summary.net >= 0 ? "+" : "−"}
            {bdtFull.format(Math.abs(summary.net))}
          </span>
        </div>
      </div>

      <div className="wallet-txn-report__table-wrap table-wrap">
        <table className="wallet-txn-report__table">
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Activity</th>
              <th scope="col">In / out</th>
              <th scope="col">Details</th>
              <th scope="col" className="wallet-txn-report__th-amount">
                Amount (BDT)
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((txn) => {
              const dir = String(txn?.direction ?? "").toUpperCase();
              const isCredit = dir === "CREDIT";
              const isDebit = dir === "DEBIT";
              const { date, time } = formatWhen(txn?.created_at);
              const amount = formatSignedAmount(txn, bdtFull);
              const details = walletTransactionReportDetails(txn);

              return (
                <tr
                  key={txn.id}
                  className={
                    isCredit ? "wallet-txn-report__row--in" : isDebit ? "wallet-txn-report__row--out" : ""
                  }
                >
                  <td className="wallet-txn-report__date" data-label="Date">
                    <span className="wallet-txn-report__date-main">{date}</span>
                    {time ? <span className="wallet-txn-report__date-time">{time}</span> : null}
                  </td>
                  <td className="wallet-txn-report__activity" data-label="Activity">
                    {walletTransactionTypeLabel(txn?.type, txn)}
                  </td>
                  <td className="wallet-txn-report__flow" data-label="In / out">
                    {isCredit ? (
                      <span className="wallet-txn-report__pill wallet-txn-report__pill--in">Credit</span>
                    ) : isDebit ? (
                      <span className="wallet-txn-report__pill wallet-txn-report__pill--out">Debit</span>
                    ) : (
                      <span className="wallet-txn-report__pill wallet-txn-report__pill--neutral">—</span>
                    )}
                  </td>
                  <td className="wallet-txn-report__details" data-label="Details" title={details}>
                    {details}
                  </td>
                  <td
                    className={`wallet-txn-report__amount ${amount.className}`.trim()}
                    data-label="Amount (BDT)"
                  >
                    {amount.text}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
